import React from 'react';
import {
    Checkbox,
    DefaultButton,
    Dropdown,
    getTheme,
    IconButton,
    IDropdownOption,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import { IExtensionFolio, IFolioQuoteLink } from '../../models/Extension';
import FieldRow from './FolioFieldRow';
import { FOLIO_EDITOR_STRINGS as S } from './folioEditorStrings';

// The PSA quote link: a button the operator adds to their PSA's quote screen that opens the folio's
// direct-import capability on that quote. Everything provider-specific about it — which capability it
// runs, the PSA's substitution tags, the setup steps — is authored here, so changing a tag Halo or
// ConnectWise expects is a folio edit rather than a release.
//
// Absent means the folio offers no quote link at all, so turning it off removes the whole block rather
// than leaving an empty one behind. What was typed is remembered while the panel is open, so switching it
// off and back on again loses nothing.

const theme = getTheme();

const emptyQuoteLink = (subExtension?: string): IFolioQuoteLink => ({
    subExtension,
    parameter: '',
    description: '',
    instructions: [],
});

/** The link CrmSetupDialog generates, so the author sees what the PSA button will be given. */
const previewLink = (folioId: string | undefined, quoteLink: IFolioQuoteLink): string =>
    `${window.location.origin}/go/opportunity-import/`
    + encodeURIComponent(`folio_${folioId || S.quoteLinkPreviewFolioId}_${quoteLink.subExtension || S.quoteLinkPreviewCapability}`)
    + (quoteLink.parameter ? `?${quoteLink.parameter}` : '');

const InstructionList: React.FC<{
    steps: string[],
    onChange: (steps: string[]) => void
}> = ({ steps, onChange }) => {
    const move = (from: number, to: number) => {
        const next = [...steps];
        const [step] = next.splice(from, 1);
        next.splice(to, 0, step);
        onChange(next);
    };

    return (
        <Stack tokens={{ childrenGap: '0.4em' }}>
            {steps.map((step, index) => (
                <FieldRow key={index} align='controls'>
                    <TextField
                        label={S.quoteLinkStep(index + 1)}
                        multiline
                        autoAdjustHeight
                        value={step}
                        onChange={(_e, v) => onChange(steps.map((s, i) => i === index ? v ?? '' : s))}
                        styles={{ root: { flexGrow: 1 } }}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Up' }}
                        title={S.quoteLinkMoveUp}
                        ariaLabel={S.quoteLinkMoveUp}
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Down' }}
                        title={S.quoteLinkMoveDown}
                        ariaLabel={S.quoteLinkMoveDown}
                        disabled={index === steps.length - 1}
                        onClick={() => move(index, index + 1)}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title={S.quoteLinkRemoveStep}
                        ariaLabel={S.quoteLinkRemoveStep}
                        onClick={() => onChange(steps.filter((_s, i) => i !== index))}
                    />
                </FieldRow>
            ))}
            <Stack horizontal>
                <DefaultButton
                    iconProps={{ iconName: 'Add' }}
                    text={S.quoteLinkAddStep}
                    onClick={() => onChange([...steps, ''])}
                />
            </Stack>
        </Stack>
    );
};

const FolioQuoteLinkEditor: React.FC<{
    folio: IExtensionFolio,
    onChange: (folio: IExtensionFolio) => void
}> = ({ folio, onChange }) => {
    const quoteLink = folio.quoteLink;
    const lastQuoteLink = React.useRef<IFolioQuoteLink | undefined>(quoteLink);
    if (quoteLink) lastQuoteLink.current = quoteLink;

    const subs = folio.subExtensions ?? {};
    // Direct-import capabilities first, since that is what a quote link runs; the rest stay pickable
    // because category is free to drift on hand-authored folios, and a declared value that names
    // something not in the folio is shown rather than silently blanked.
    const capabilityOptions: IDropdownOption[] = React.useMemo(() => {
        const entries = Object.entries(subs);
        const ordered = [
            ...entries.filter(([, s]) => s.category === 'directImport'),
            ...entries.filter(([, s]) => s.category !== 'directImport'),
        ];
        const options: IDropdownOption[] = ordered.map(([key, s]) => ({
            key,
            text: s.name ? `${s.name} (${key})` : key,
        }));
        const declared = quoteLink?.subExtension;
        if (declared && !subs[declared]) options.push({ key: declared, text: S.quoteLinkMissingCapability(declared) });
        return options;
    }, [subs, quoteLink?.subExtension]);

    const set = (next: IFolioQuoteLink) => onChange({ ...folio, quoteLink: next });

    const toggle = (on: boolean) => {
        if (!on) {
            const rest = { ...folio };
            delete rest.quoteLink;
            onChange(rest);
            return;
        }
        const firstDirectImport = Object.entries(subs).find(([, s]) => s.category === 'directImport')?.[0];
        set(lastQuoteLink.current ?? emptyQuoteLink(firstDirectImport));
    };

    return (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            <Checkbox
                label={S.quoteLinkEnabled}
                checked={!!quoteLink}
                onChange={(_e, checked) => toggle(!!checked)}
            />
            {quoteLink && (
                <>
                    <Dropdown
                        label={S.quoteLinkCapability}
                        selectedKey={quoteLink.subExtension ?? null}
                        options={capabilityOptions}
                        placeholder={S.quoteLinkCapabilityPlaceholder}
                        onChange={(_e, o) => set({ ...quoteLink, subExtension: o ? String(o.key) : undefined })}
                        styles={{ root: { maxWidth: 480 } }}
                    />
                    <TextField
                        label={S.quoteLinkParameter}
                        value={quoteLink.parameter ?? ''}
                        onChange={(_e, v) => set({ ...quoteLink, parameter: v ?? '' })}
                        description={S.quoteLinkParameterHelp}
                        placeholder='opp={recid}'
                        styles={{ field: { fontFamily: 'monospace' } }}
                    />
                    <Stack tokens={{ childrenGap: '0.2em' }}>
                        <Text variant='small' style={{ fontWeight: 600 }}>{S.quoteLinkPreview}</Text>
                        <Text
                            variant='small'
                            style={{ fontFamily: 'monospace', wordBreak: 'break-all', color: theme.palette.neutralSecondary }}
                        >
                            {previewLink(folio.id, quoteLink)}
                        </Text>
                    </Stack>
                    <TextField
                        label={S.quoteLinkDescription}
                        multiline
                        rows={2}
                        value={quoteLink.description ?? ''}
                        onChange={(_e, v) => set({ ...quoteLink, description: v ?? '' })}
                        description={S.quoteLinkDescriptionHelp}
                    />

                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Text style={{ fontWeight: 600 }}>{S.quoteLinkInstructions}</Text>
                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                            {S.quoteLinkInstructionsHelp}
                        </Text>
                        <InstructionList
                            steps={quoteLink.instructions ?? []}
                            onChange={(steps) => set({ ...quoteLink, instructions: steps })}
                        />
                    </Stack>

                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Text style={{ fontWeight: 600 }}>{S.quoteLinkAutoInstall}</Text>
                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                            {S.quoteLinkAutoInstallHelp}
                        </Text>
                        <TextField
                            label={S.quoteLinkPermissionUrl}
                            value={quoteLink.permissionUrl ?? ''}
                            onChange={(_e, v) => set({ ...quoteLink, permissionUrl: v || undefined })}
                        />
                        <TextField
                            label={S.quoteLinkCreateUrl}
                            value={quoteLink.createUrl ?? ''}
                            onChange={(_e, v) => set({ ...quoteLink, createUrl: v || undefined })}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default FolioQuoteLinkEditor;
